import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger
} from '@radix-ui/react-accordion';
import { format } from 'date-fns';
import {
  CalendarCheckIcon,
  ChevronDownIcon,
  CircleCheckIcon,
  CircleIcon
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { ProgressBar } from 'react-aria-components';

import Header from '../Header';
import {
  areAllFundStatusesPassed,
  DUE_DILIGENCE_STAGES_REQUIRING_ALL_FUNDS_PASSED,
  GET_DEAL_OPPORTUNITY_IC_FUND_RESULT
} from '../icFundResult';
import type { GetDealOpportunityIcFundResult } from '../icFundResult';
import { RightSidebarWrapper } from '../RightSidebar/RightSidebarWrapper';
import useOutletContext from '../useOutletContext';

import { TruncatedTextTooltip } from '@/components/TruncatedTextTooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Spinner } from '@/components/ui/spinner';
import { formatRelativeTime } from '@/utils/date';

interface GetDealOpportunityDueDiligence {
  node: {
    __typename: 'OpportunityNode';
    dueDiligenceProjectId: {
      id: string;
      name: null | string;
      nextMilestoneId: {
        id: string;
        isReached: boolean | null;
        name: null | string;
      } | null;
      rootTasks: {
        childIds:
          | {
              completionDate: null | string;
              dateEnd: null | string;
              endDateClosingDate: null | string;
              endDay: null | number;
              id: string;
              name: null | string;
              startDateClosingDate: null | string;
              startDay: null | number;
              state: null | string;
            }[]
          | null;
        completionDate: null | string;
        endDateClosingDate: null | string;
        endDay: null | number;
        id: string;
        milestoneId: {
          id: string;
          inputReachedDate: null | string;
          isReached: boolean | null;
          name: null | string;
          reachedDate: null | string;
        } | null;
        name: string;
        startDateClosingDate: null | string;
        startDay: null | number;
        subtaskCompletionPercentage: number;
      }[];
    } | null;
    id: string;
    name: null | string;
    stage: null | string;
  };
}

const GET_DEAL_OPPORTUNITY_DUE_DILIGENCE = gql`
  query DealOpportunityDueDiligence($id: ID!) {
    node(id: $id) {
      ... on OpportunityNode {
        id
        name
        stage
        dueDiligenceProjectId {
          id
          name
          nextMilestoneId {
            id
            name
            isReached
          }
          rootTasks {
            id
            name
            completionDate
            subtaskCompletionPercentage
            startDay
            endDay
            startDateClosingDate
            endDateClosingDate
            milestoneId {
              id
              name
              isReached
              reachedDate
              inputReachedDate
            }
            childIds {
              id
              name
              dateEnd
              completionDate
              state
              startDay
              endDay
              startDateClosingDate
              endDateClosingDate
            }
          }
        }
      }
    }
  }
`;

const MARK_PROJECT_TASK_AS_DONE = gql`
  mutation MarkProjectTaskAsDone(
    $task_id: ID!
    $completion_date: Date
    $state: String
  ) {
    updateTask(
      id: $task_id
      values: { state: $state, completionDate: $completion_date }
    ) {
      ok
    }
  }
`;

const UPDATE_TASK_COMPLETION_DATE = gql`
  mutation UpdateTaskCompletionDate($task_id: ID!, $completion_date: Date) {
    updateTask(id: $task_id, values: { completionDate: $completion_date }) {
      ok
    }
  }
`;

const MARK_MILESTONE_TASK_AS_REACHED = gql`
  mutation MarkMilestoneTaskAsReached(
    $milestone_id: ID!
    $reached_date: Date
    $is_reached: Boolean
  ) {
    updateMilestone(
      id: $milestone_id
      values: { isReached: $is_reached, inputReachedDate: $reached_date }
    ) {
      ok
    }
  }
`;

const MOVE_DEAL_TO_MANDATED = gql`
  mutation MoveDealToMandated($dealType: String!, $dealId: ID!) {
    moveDealToMandated(dealType: $dealType, dealId: $dealId) {
      ok
      deal
      missingFields
    }
  }
`;

const MANDATED_SUBTASK_NAME =
  'Verify required closing fields are complete before moving to Mandated: Best Estimate Date, Earliest Date, Latest Date, SMC Close Date Flexibility, Closing Likelihood, Smaller Enterprise';

const InvestmentsDirectIdDueDiligence = () => {
  const { id } = useOutletContext();

  const { data: data1, loading: loading1 } = useQuery<
    GetDealOpportunityDueDiligence,
    { id: string }
  >(GET_DEAL_OPPORTUNITY_DUE_DILIGENCE, {
    variables: { id }
  });

  const { data: icFundResultData } = useQuery<
    GetDealOpportunityIcFundResult,
    { id: string }
  >(GET_DEAL_OPPORTUNITY_IC_FUND_RESULT, {
    variables: { id }
  });

  const allFundStatusesPassed = areAllFundStatusesPassed(
    icFundResultData?.node.icFundResult
  );

  const [markProjectTaskAsDone] = useMutation(MARK_PROJECT_TASK_AS_DONE, {
    refetchQueries: [
      { query: GET_DEAL_OPPORTUNITY_DUE_DILIGENCE, variables: { id } }
    ]
  });

  const [updateTaskCompletionDate] = useMutation(UPDATE_TASK_COMPLETION_DATE, {
    refetchQueries: [
      { query: GET_DEAL_OPPORTUNITY_DUE_DILIGENCE, variables: { id } }
    ]
  });

  const [markMilestoneTaskAsReached] = useMutation(
    MARK_MILESTONE_TASK_AS_REACHED,
    {
      refetchQueries: [
        { query: GET_DEAL_OPPORTUNITY_DUE_DILIGENCE, variables: { id } }
      ]
    }
  );

  const [moveDealToMandated] = useMutation(MOVE_DEAL_TO_MANDATED);
  const defaultOpenItems = useMemo(() => {
    const nextMilestoneId =
      data1?.node.dueDiligenceProjectId?.nextMilestoneId?.id;
    return (
      data1?.node.dueDiligenceProjectId?.rootTasks
        .filter(item => item.milestoneId?.id === nextMilestoneId)
        .map(item => item.name) ?? []
    );
  }, [data1]);

  const [openCompletionDatePopoverTaskId, setOpenCompletionDatePopoverTaskId] =
    useState<null | string>(null);
  const [
    openCompletionDatePopoverMilestoneId,
    setOpenCompletionDatePopoverMilestoneId
  ] = useState<null | string>(null);

  if (loading1) {
    return (
      <div className="-mt-16 grid h-[calc(100%+64px)] place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  if (!data1) {
    return <div>{'Error'}</div>;
  }

  return (
    <>
      <Header />
      <RightSidebarWrapper>
        <Accordion
          className="bg-card min-w-0 space-y-2.5 px-5 pt-5 pb-16.5 md:pl-14"
          defaultValue={defaultOpenItems}
          type="multiple"
        >
          {data1.node.dueDiligenceProjectId?.rootTasks.map(a => {
            const isStageRequiringAllFundsPassed = (
              DUE_DILIGENCE_STAGES_REQUIRING_ALL_FUNDS_PASSED as readonly string[]
            ).includes(a.name);
            const stageCompletionDate =
              a.milestoneId?.inputReachedDate ?? a.milestoneId?.reachedDate;
            const parsedStageCompletionDate = stageCompletionDate
              ? new Date(stageCompletionDate)
              : null;

            const isStageDisabled =
              data1.node.stage === 'inactive' ||
              (isStageRequiringAllFundsPassed && !allFundStatusesPassed);

            return (
              <AccordionItem
                className="bg-card rounded-md border p-2.5"
                key={a.id}
                value={a.name}
              >
                <AccordionHeader>
                  <AccordionTrigger className="group flex w-full items-center justify-between">
                    <div className="flex min-w-0 items-center gap-x-2.5">
                      <ChevronDownIcon
                        aria-hidden
                        className="shrink-0 transition-transform group-data-[state=open]:-rotate-z-180"
                        size={16}
                      />
                      <div className="text-sm font-medium break-words">
                        {a.name}
                      </div>
                      <div className="flex shrink-0 gap-x-2.25">
                        <ProgressBar
                          aria-label="Task progress"
                          style={{ height: 16, width: 16 }}
                          value={a.subtaskCompletionPercentage * 100}
                        >
                          {({ percentage }) => (
                            <svg
                              fill="none"
                              height="100%"
                              viewBox="0 0 32 32"
                              width="100%"
                            >
                              <circle
                                cx="50%"
                                cy="50%"
                                // Normalize the path length to 100 so we can easily set stroke-dashoffset to a percentage.
                                pathLength="100"
                                r="calc(50% - 2px)"
                                stroke="#3b82f6"
                                // Add extra gap between dashes so 0% works in Chrome.
                                strokeDasharray="100 200"
                                strokeDashoffset={100 - (percentage ?? 0)}
                                strokeLinecap="round"
                                strokeWidth={4}
                                style={{
                                  rotate: '-90deg',
                                  transformOrigin: 'center center'
                                }}
                              />
                            </svg>
                          )}
                        </ProgressBar>
                        <div className="text-xs text-blue-500">
                          {a.subtaskCompletionPercentage *
                            (a.childIds?.length ?? 0)}
                          /{a.childIds?.length}
                        </div>
                      </div>
                    </div>
                    {a.subtaskCompletionPercentage === 1 &&
                      a.milestoneId &&
                      !a.milestoneId.isReached && (
                        <Button
                          disabled={isStageDisabled}
                          onClick={e => {
                            e.stopPropagation();

                            void (async () => {
                              try {
                                if (a.milestoneId) {
                                  await markMilestoneTaskAsReached({
                                    variables: {
                                      is_reached: true,
                                      milestone_id: a.milestoneId.id,
                                      reached_date: format(
                                        new Date(),
                                        'yyyy-MM-dd'
                                      )
                                    }
                                  });
                                }
                              } catch (error) {
                                console.error(error);
                              }
                            })();
                          }}
                        >
                          Complete Stage
                        </Button>
                      )}
                    {a.milestoneId?.isReached && (
                      <div
                        className="text-muted-foreground flex items-center gap-x-2.5 text-sm"
                        onClick={e => {
                          e.stopPropagation();
                        }}
                      >
                        <div>{`${a.name} Stage done on`}</div>
                        <Popover
                          onOpenChange={open => {
                            setOpenCompletionDatePopoverMilestoneId(
                              open ? (a.milestoneId?.id ?? null) : null
                            );
                          }}
                          open={
                            openCompletionDatePopoverMilestoneId ===
                            a.milestoneId.id
                          }
                        >
                          <PopoverTrigger asChild>
                            <Badge
                              className="cursor-pointer"
                              variant="pastel-slate"
                            >
                              <CalendarCheckIcon />
                              {parsedStageCompletionDate
                                ? format(
                                    parsedStageCompletionDate,
                                    'MMM d, yyyy'
                                  )
                                : 'Set date'}
                            </Badge>
                          </PopoverTrigger>
                          <PopoverContent
                            align="start"
                            className="w-auto p-0"
                          >
                            <Calendar
                              defaultMonth={
                                parsedStageCompletionDate ?? new Date()
                              }
                              disabled={{ after: new Date() }}
                              mode="single"
                              onSelect={selectedDate => {
                                const { milestoneId } = a;

                                if (!selectedDate || !milestoneId) return;

                                setOpenCompletionDatePopoverMilestoneId(null);

                                void (async () => {
                                  try {
                                    await markMilestoneTaskAsReached({
                                      variables: {
                                        is_reached: true,
                                        milestone_id: milestoneId.id,
                                        reached_date: format(
                                          selectedDate,
                                          'yyyy-MM-dd'
                                        )
                                      }
                                    });
                                  } catch (error) {
                                    console.error(error);
                                  }
                                })();
                              }}
                              selected={parsedStageCompletionDate ?? undefined}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    )}
                  </AccordionTrigger>
                </AccordionHeader>
                <AccordionContent className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down mt-5 overflow-hidden">
                  {a.childIds?.map(childTask => (
                    <div
                      className="flex h-8.5 justify-between gap-x-1 px-2.5 py-1.5"
                      key={childTask.id}
                    >
                      <div className="flex min-w-0 items-center gap-x-1">
                        {childTask.completionDate ? (
                          !a.milestoneId?.isReached ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  aria-label="Mark task as to-do"
                                  className="shrink-0 cursor-pointer"
                                  type="button"
                                >
                                  <CircleCheckIcon
                                    className="text-white"
                                    fill="#003455"
                                    size={18}
                                  />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start">
                                <DropdownMenuItem
                                  onSelect={() => {
                                    setOpenCompletionDatePopoverTaskId(null);

                                    void (async () => {
                                      try {
                                        await markProjectTaskAsDone({
                                          variables: {
                                            completion_date: null,
                                            state: '01_in_progress',
                                            task_id: childTask.id
                                          }
                                        });
                                      } catch (error) {
                                        console.error(error);
                                      }
                                    })();
                                  }}
                                >
                                  <CircleIcon
                                    className="text-border"
                                    size={14}
                                  />
                                  To-do
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : (
                            <CircleCheckIcon
                              className="shrink-0 text-white"
                              fill="#003455"
                              size={18}
                            />
                          )
                        ) : (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                aria-label="Mark task as done"
                                className="shrink-0 cursor-pointer p-0.5 disabled:cursor-not-allowed disabled:opacity-50"
                                disabled={isStageDisabled}
                                type="button"
                              >
                                <CircleIcon
                                  className="text-border"
                                  size={14}
                                />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                              <DropdownMenuItem
                                disabled={isStageDisabled}
                                onSelect={() => {
                                  if (isStageDisabled) return;

                                  void (async () => {
                                    try {
                                      await markProjectTaskAsDone({
                                        variables: {
                                          completion_date: format(
                                            new Date(),
                                            'yyyy-MM-dd'
                                          ),
                                          state: '1_done',
                                          task_id: childTask.id
                                        }
                                      });

                                      // Trigger moveDealToMandated when specific subtask is completed
                                      if (
                                        childTask.name === MANDATED_SUBTASK_NAME
                                      ) {
                                        await moveDealToMandated({
                                          variables: {
                                            dealId: id,
                                            dealType: 'opportunity'
                                          }
                                        });
                                      }
                                    } catch (error) {
                                      console.error(error);
                                    }
                                  })();
                                }}
                              >
                                <CircleCheckIcon
                                  className="text-white"
                                  fill="#003455"
                                  size={18}
                                />
                                Done
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        <TruncatedTextTooltip className="min-w-0 text-sm">
                          {childTask.name}
                        </TruncatedTextTooltip>
                        {(() => {
                          if (childTask.completionDate) {
                            const completionDate = new Date(
                              childTask.completionDate
                            );
                            return (
                              <Popover
                                onOpenChange={open => {
                                  setOpenCompletionDatePopoverTaskId(
                                    open ? childTask.id : null
                                  );
                                }}
                                open={
                                  openCompletionDatePopoverTaskId ===
                                  childTask.id
                                }
                              >
                                <PopoverTrigger asChild>
                                  <Badge
                                    className="cursor-pointer"
                                    variant="pastel-green"
                                  >
                                    <CalendarCheckIcon />
                                    {format(completionDate, 'MMM d, yyyy')}
                                  </Badge>
                                </PopoverTrigger>
                                <PopoverContent
                                  align="start"
                                  className="w-auto p-0"
                                >
                                  <Calendar
                                    defaultMonth={completionDate}
                                    disabled={{ after: new Date() }}
                                    mode="single"
                                    onSelect={selectedDate => {
                                      if (!selectedDate) return;

                                      setOpenCompletionDatePopoverTaskId(null);

                                      void (async () => {
                                        try {
                                          await updateTaskCompletionDate({
                                            variables: {
                                              completion_date: format(
                                                selectedDate,
                                                'yyyy-MM-dd'
                                              ),
                                              task_id: childTask.id
                                            }
                                          });
                                        } catch (error) {
                                          console.error(error);
                                        }
                                      })();
                                    }}
                                    selected={completionDate}
                                  />
                                </PopoverContent>
                              </Popover>
                            );
                          }

                          if (!childTask.endDateClosingDate) {
                            return null;
                          }
                          const endDate = new Date(
                            childTask.endDateClosingDate
                          );
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          endDate.setHours(0, 0, 0, 0);
                          const diffTime = endDate.getTime() - today.getTime();
                          const diffDays = Math.ceil(
                            diffTime / (1000 * 60 * 60 * 24)
                          );

                          let variant:
                            | 'pastel-green'
                            | 'pastel-orange'
                            | 'pastel-red';
                          if (diffDays < 0) {
                            variant = 'pastel-red';
                          } else if (diffDays <= 2) {
                            variant = 'pastel-orange';
                          } else {
                            variant = 'pastel-green';
                          }

                          return (
                            <Badge
                              className="shrink-0"
                              variant={variant}
                            >
                              <CalendarCheckIcon />
                              {format(endDate, 'MMM d, yyyy')}
                            </Badge>
                          );
                        })()}
                      </div>
                      <div className="flex shrink-0 items-center">
                        <div className="text-muted-foreground mr-2 text-sm">
                          {formatRelativeTime(childTask.completionDate, 'days')}
                        </div>
                      </div>
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </RightSidebarWrapper>
    </>
  );
};

export default InvestmentsDirectIdDueDiligence;
